import {faCircleInfo} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Space} from 'antd';

import {Text} from '../../../../../../component/text/text';
import {gray7} from '../../../../../../css/var-export.scss';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {CompanyKeyEnum, ProtocolEnum} from '../../../../../../service/company-v2/company-const';
import {CompanyFormType} from '../../../../../../service/company-v2/company-type';
import {CompanyFormSocialNetworkType} from '../../../../../../service/company-v2/types/company-links-type';
import {Form} from '../../../../../../typings/antd';
import {ProvidersIdsEnum} from '../../../../../../util/type';
import {CatalogConfigWithIndexType} from '../../catalog-list/catalog-list';
import {CatalogTab} from '../../catalog-list/catalog-tab';

import {LinksFormItem} from './links-form-item/links-form-item';

type PropsType = {
    name: string;
    catalog: CatalogConfigWithIndexType;
    hasInitialSocialNetworks: boolean;
    hasInitialWebsites: boolean;
    handleRemoveCatalog: (catalogId: number) => void;
};

export function CatalogLinks(props: PropsType): JSX.Element {
    const {name, catalog, hasInitialSocialNetworks, hasInitialWebsites, handleRemoveCatalog} = props;

    const {getLocalizedString} = useLocale();
    const form = Form.useFormInstance<CompanyFormType>();

    const socialNetworksPath = [CompanyKeyEnum.ExtraSocialNetworks, catalog.index, 'socialNetworks'] as const;
    // Form.useWatch returns undefined until its effect runs after the first render,
    // so fall back to the form's current value to avoid a flash of the wrong (hint-hidden) state.
    const catalogSocialNetworks: Array<CompanyFormSocialNetworkType> | undefined =
        Form.useWatch(socialNetworksPath, form) ?? form.getFieldValue(socialNetworksPath);

    // Both MAX-related checks (the side hint and the disabled dropdown option)
    // derive from this same count, computed once here and passed down.
    const maxLinksCount = (catalogSocialNetworks ?? []).filter((link) => link?.protocol === ProtocolEnum.MAX).length;
    const isDoubleGisCatalog = catalog.catalogId === ProvidersIdsEnum.doubleGis;
    const hasMaxLink = isDoubleGisCatalog && maxLinksCount > 0;

    function handleRemove() {
        handleRemoveCatalog(catalog.catalogId);

        form.setFieldsValue({
            extraWebsites: form
                .getFieldValue(CompanyKeyEnum.ExtraWebsites)
                .filter(({catalogId}) => catalogId !== catalog.catalogId),
        });

        form.setFieldsValue({
            extraSocialNetworks: form
                .getFieldValue(CompanyKeyEnum.ExtraSocialNetworks)
                .filter(({catalogId}) => catalogId !== catalog.catalogId),
        });
    }

    return (
        <CatalogTab catalog={catalog} modalFieldName={name} onRemove={handleRemove}>
            <Form.List name={CompanyKeyEnum.ExtraWebsites}>
                {() => (
                    <LinksFormItem
                        hasInitialLinks={hasInitialWebsites}
                        label={getLocalizedString('COMPANY_FORM__LINKS__WEBSITES__LABEL')}
                        name={[CompanyKeyEnum.ExtraWebsites, catalog.index, 'websites']}
                        placeholder={getLocalizedString('COMPANY_FORM__LINKS__WEBSITES__PLACEHOLDER')}
                    />
                )}
            </Form.List>

            <Form.List name={CompanyKeyEnum.ExtraSocialNetworks}>
                {() => (
                    <LinksFormItem
                        catalogId={catalog.catalogId}
                        hasInitialLinks={hasInitialSocialNetworks}
                        httpsOnly
                        label={getLocalizedString('COMPANY_FORM__LINKS__SOCIAL_NETWORKS__LABEL')}
                        name={[CompanyKeyEnum.ExtraSocialNetworks, catalog.index, 'socialNetworks']}
                        placeholder={getLocalizedString('COMPANY_FORM__LINKS__SOCIAL_NETWORKS__PLACEHOLDER')}
                        sideHint={
                            hasMaxLink && (
                                <Space align="start">
                                    <FontAwesomeIcon color={gray7} icon={faCircleInfo} />
                                    <Text secondary small stringKey="COMPANY_FORM__LINKS__SOCIAL_NETWORKS__MAX_HINT" />
                                </Space>
                            )
                        }
                    />
                )}
            </Form.List>
        </CatalogTab>
    );
}
